export const ONBOARDING_PREFERENCE_VERSION = 1 as const;

export type CreatorGuideStep =
  | 'station'
  | 'first_piece'
  | 'uphill_slope'
  | 'powered_choice'
  | 'powered_transition'
  | 'powered_climb'
  | 'level_off_choice'
  | 'level_off_place'
  | 'turn_back_choice'
  | 'turn_back_place'
  | 'downhill_choice'
  | 'downhill_transition'
  | 'downhill_run'
  | 'downhill_run_finish'
  | 'downhill_level_choice'
  | 'downhill_level_place'
  | 'special_tab'
  | 'barrel_roll_choice'
  | 'barrel_roll_place'
  | 'regular_tab'
  | 'endpoint'
  | 'route_guide'
  | 'photo_choice'
  | 'photo_place'
  | 'bank_home_choice'
  | 'bank_home_place'
  | 'turn_home_choice'
  | 'turn_home_place'
  | 'bank_straight_choice'
  | 'bank_level_choice'
  | 'bank_level_place'
  | 'undo_demo'
  | 'redo_demo'
  | 'brake_choice'
  | 'brake_run'
  | 'ride_check'
  | 'test_ride'
  | 'unlocks';

export type FinishLessonStep =
  'endpoint' | 'route_guide' | 'close_track' | 'ride_check' | 'brake' | 'test_ride';

export interface OnboardingPreferenceV1 {
  readonly version: 1;
  readonly creatorGuide: 'not_started' | 'completed' | 'skipped';
  readonly finishLesson: 'not_offered' | 'dismissed' | 'started' | 'completed';
  readonly riderInitialSeen: boolean;
  readonly riderSocialSeen: boolean;
}

export interface RuntimeOnboardingState {
  readonly creator: {
    readonly active: boolean;
    readonly step: CreatorGuideStep | null;
    readonly status: OnboardingPreferenceV1['creatorGuide'];
    readonly finishOfferVisible: boolean;
  };
  readonly finishLesson: {
    readonly active: boolean;
    readonly step: FinishLessonStep | null;
    readonly status: OnboardingPreferenceV1['finishLesson'];
  };
  readonly rider: {
    readonly initialSeen: boolean;
    readonly socialSeen: boolean;
  };
}

export const DEFAULT_ONBOARDING_PREFERENCE: OnboardingPreferenceV1 = {
  version: ONBOARDING_PREFERENCE_VERSION,
  creatorGuide: 'not_started',
  finishLesson: 'not_offered',
  riderInitialSeen: false,
  riderSocialSeen: false,
};

export function parseOnboardingPreference(value: unknown): OnboardingPreferenceV1 {
  if (!value || typeof value !== 'object') return { ...DEFAULT_ONBOARDING_PREFERENCE };
  const candidate = value as Partial<OnboardingPreferenceV1>;
  if (candidate.version !== ONBOARDING_PREFERENCE_VERSION)
    return { ...DEFAULT_ONBOARDING_PREFERENCE };
  const creatorGuide = ['not_started', 'completed', 'skipped'].includes(
    String(candidate.creatorGuide),
  )
    ? (candidate.creatorGuide as OnboardingPreferenceV1['creatorGuide'])
    : 'not_started';
  const finishLesson = ['not_offered', 'dismissed', 'started', 'completed'].includes(
    String(candidate.finishLesson),
  )
    ? (candidate.finishLesson as OnboardingPreferenceV1['finishLesson'])
    : 'not_offered';
  return {
    version: ONBOARDING_PREFERENCE_VERSION,
    creatorGuide,
    finishLesson,
    riderInitialSeen: candidate.riderInitialSeen === true,
    riderSocialSeen: candidate.riderSocialSeen === true,
  };
}
